import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/joy/Link";
import { useColorScheme } from "@mui/joy";

export default function TermsOfService() {
  const year = new Date().getFullYear();
  const { mode } = useColorScheme();
  const dark = mode === "dark";

  const currentDate = new Date().toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",        
      }}
    >
      
        {/* Hero Section */}
        <Box
          sx={{
            px: { xs: 3, md: 5 },
            py: 5,
            background: dark
              ? "linear-gradient(to right, #4c1d95, #1e3a8a)"
              : "linear-gradient(to right, #8b5cf6, #38BDF8)",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              fontWeight: "bold",
              mb: 1,
              color: "#f3f4f6",
            }}
          >
            Terms of Service
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "1.1rem", opacity: 0.9, color: "#f3f4f6" }}>
            Effective Date: {currentDate}
          </Typography>
        </Box>

        
        {/* Main Content */}
        <Box sx={{ px: { xs: 3, md: 12 }, py: 5 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              Overview
            </Typography>
            <Typography sx={{ mb: 2 }}>
              Welcome to <strong>HackTheWay</strong>, a cybersecurity platform fostering
              learning and engagement through Capture the Flag (CTF) challenges, Bug
              Bounty events, and more. By using HackTheWay, you agree to comply with
              these Terms and Conditions. Please read them carefully.
            </Typography>
            <Typography>
              <strong>HackTheWay</strong> is solely designed for educational purposes
              and does not support or promote malicious activities. Users are solely
              responsible for their actions outside the platform.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography sx={{ mb:1 }}>
              1.1. By registering and participating in events hosted on HackTheWay, you agree to these Terms and Conditions, our Privacy Policy, and any other applicable guidelines provided by HackTheWay or event organizers.
            </Typography>
            <Typography sx={{ mb:1 }}>
              1.2. If you do not agree with these terms, please refrain from using HackTheWay’s services.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              2. User Registration and Account Security
            </Typography>
            <Typography sx={{ mb:1 }}>
              2.1. To access events or features on HackTheWay, you must create an account with accurate and complete information.
            </Typography>
            <Typography sx={{ mb:1 }}>
              2.2. You are solely responsible for maintaining the confidentiality of your account credentials and must notify us immediately of any unauthorized use of your account.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              3. Data Collection and Sharing
            </Typography>
            <Typography sx={{ mb:1 }}>
              3.1. Data Usage: When registering for events, HackTheWay collects personal
              data such as name, email address, and any other event-specific details
              requested by organizers.
            </Typography>
            <Typography sx={{ mb:1 }}>
              3.2. Limited Sharing: This data will only be shared with the organizer of
              the event you participate in. HackTheWay will not distribute your personal
              data to third parties beyond the event organizer.
            </Typography>
            <Typography sx={{ mb:1 }}>
              3.3. Organizer’s Responsibility: Event organizers are independently
              responsible for handling your data in compliance with applicable laws.
              HackTheWay disclaims liability for how event organizers use or manage this
              data.
            </Typography>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              4. Prohibited Activities</Typography>
            <Typography sx={{ mb:1 }}>You agree not to:<br />
              <Box component="ul" sx={{ pl: 3, listStyle: "disc" }}>
                <li>
                  Upload, distribute, or transmit any malicious code, unauthorized files, or content that could harm the platform or other users.
                </li>
                <li>Attempt to gain unauthorized access to HackTheWay’s systems or other users’ accounts.</li>
                <li>
                  Use the platform to violate any local, national, or international laws or regulations.
                </li>
              </Box>
            </Typography>

          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              5. Event Content and User Contributions</Typography>
            <Typography sx={{ mb:1 }}>
              5.1. Ownership: Any content (such as challenges or solutions) uploaded by users or organizers remains the intellectual property of its creators. By uploading, you grant HackTheWay a non-exclusive license to host and display this content.
            </Typography>
            <Typography sx={{ mb:1 }}>
              5.2. Prohibited Sharing: Sharing solutions or other sensitive event materials outside of HackTheWay, unless explicitly permitted, is strictly prohibited.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              6. Limitation of Liability</Typography>
            <Typography sx={{ mb:1 }}>
              6.1. HackTheWay provides the platform “as is” without any warranties or guarantees of uninterrupted service.
            </Typography>
            <Typography sx={{ mb:1 }}>
              6.2. HackTheWay is not responsible for:
              <Box component="ul" sx={{ pl: 3, listStyle: "disc" }}>
                <li>
                  Data misuse by event organizers.
                </li>
                <li>Disputes regarding event content, results, or prizes.
                </li>
                <li>
                  Security incidents arising from user negligence.
                </li>
              </Box>
            </Typography>
            <Typography sx={{ mb:1 }}>
              6.3. Responsibility for Actions: HackTheWay is not responsible for any malicious activities performed by its users. Each user is solely responsible for their actions and their consequences, including any legal or ethical implications resulting from their misuse of the platform.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              7. Intellectual Property</Typography>
            <Typography sx={{ mb:1 }}>
              All content, branding, software, and materials on HackTheWay are protected by intellectual property laws. Unauthorized reproduction or use is strictly prohibited.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              8. Service Availability</Typography>
            <Typography sx={{ mb:1 }}>
              HackTheWay strives to ensure the platform’s availability but reserves the right to suspend or terminate services for maintenance, updates, or unforeseen circumstances without prior notice.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              9. Privacy and Cookies</Typography>
            <Typography sx={{ mb:1 }}>
              9.1. HackTheWay uses cookies to improve the user experience. By using our platform, you consent to our use of cookies as detailed in our Privacy Policy.
            </Typography>
            <Typography sx={{ mb:1 }}>
              9.2. HackTheWay processes user data per applicable privacy laws.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              10. Governing Law and Dispute Resolution</Typography>
            <Typography sx={{ mb:1 }}>
              These terms are governed by the laws of India. Any disputes shall be resolved in the courts of India.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              11. Modifications to Terms</Typography>
            <Typography sx={{ mb:1 }}>
              HackTheWay reserves the right to modify these Terms and Conditions at any time. Continued use of the platform after updates indicates acceptance of the revised terms.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              12. Educational Purpose Disclaimer</Typography>
            <Typography sx={{ mb:1 }}>
              HackTheWay is designed solely for educational purposes to enhance users' cybersecurity skills and knowledge. The platform, its content, and its events are not intended to promote or facilitate malicious activities.
              <Box component="ul" sx={{ pl: 3, listStyle: "disc" }}>
                <li>
                  Users are expected to act ethically and abide by applicable laws and regulations while participating in events or using the platform.
                </li>
                <li>
                  Any misuse of the platform or its resources for malicious or unauthorized purposes is strictly prohibited and will result in account termination and potential legal consequences.
                </li>
              </Box>
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
              13. Contact Information</Typography>
            <Typography sx={{ mb:1 }}>
              For inquiries about these Terms and Conditions, contact us at <Link>support@hacktheway.com</Link> .
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <footer className="bg-card p-8 rounded-lg">
              <p className="text-muted-foreground text-center">
                © HackTheWay {new Date().getFullYear()}, All rights reserved • Privacy
                Policy
              </p>
            </footer>
          </Box>
        </Box>
      </Box>    
  );
}
